import { RawDraftContentState } from 'draft-js'
import { enumNarrow } from '../helpers/enumGuard'
import { IARAPReconcileSummary, IBorrowingBase, OngoingReportingType, TemplateTypes } from './bbc'
import { IFile } from './box'
import { ClientERPSystem, IClientInfo } from './client'
import { IClientEntityRelationship } from './integration'
import { ITag } from './notes'
import { IUser, UserStatus } from './user'

export type IDocumentSections = {
  'The Team': IDueDiligenceDocumentRequestType[]
  'Financials & Collateral': IDueDiligenceDocumentRequestType[]
  'Company Background': IDueDiligenceDocumentRequestType[]
  'Third Party Testing - Samples': IDueDiligenceDocumentRequestType[]
}
export interface ISalesforceDueDiligenceInfo {
  id: string
  clientName: string
  clientInfo?: IClientInfo
  closeDate?: string
  age?: number
  boxLink?: string
  salesforceLink?: string
  commitmentAmount?: number
}

export enum DueDiligenceProgressStatus {
  NotStarted = 'Not started',
  InProgress = 'In progress',
  Completed = 'Completed',
  NotApplicable = 'Not applicable',
}

export enum DueDiligenceProgressStep {
  DiligenceDeposit = 'Diligence deposit',
  Application = 'Application',
  DiligenceCalls = 'Diligence calls',
  ThirdPartyExam = 'Third party exam',
  CreditApproval = 'Credit approval',
  ContractFinalized = 'Contract finalized',
  CollateralDocs = 'Collateral docs',
  FirstFunding = 'First funding',
}

export interface IDueDiligenceProgress {
  [DueDiligenceProgressStep.DiligenceDeposit]: DueDiligenceProgressStatus
  [DueDiligenceProgressStep.Application]: DueDiligenceProgressStatus
  [DueDiligenceProgressStep.DiligenceCalls]: DueDiligenceProgressStatus
  [DueDiligenceProgressStep.ThirdPartyExam]: DueDiligenceProgressStatus
  [DueDiligenceProgressStep.CreditApproval]: DueDiligenceProgressStatus
  [DueDiligenceProgressStep.ContractFinalized]: DueDiligenceProgressStatus
  [DueDiligenceProgressStep.CollateralDocs]: DueDiligenceProgressStatus
  [DueDiligenceProgressStep.FirstFunding]: DueDiligenceProgressStatus
}

export interface IDiligenceDepositDetails {
  depositAmount?: number | null
  depositReceivedAt?: string | null
  depositReference?: string | null
}

export interface IDueDiligenceInfo extends IDiligenceDepositDetails {
  id: string
  clientName: string
  clientInfo?: IClientInfo
  progress: IDueDiligenceProgress
}

export const DEFAULT_DOCUMENT_SECTIONS_ORDER = [
  'Company Background',
  'The Team',
  'Financials & Collateral',
  'Third Party Testing - Samples',
]

export const DEFAULT_DUE_DILIGENCE_PROGRESS = {
  [DueDiligenceProgressStep.DiligenceDeposit]: DueDiligenceProgressStatus.NotStarted,
  [DueDiligenceProgressStep.Application]: DueDiligenceProgressStatus.NotStarted,
  [DueDiligenceProgressStep.DiligenceCalls]: DueDiligenceProgressStatus.NotStarted,
  [DueDiligenceProgressStep.ThirdPartyExam]: DueDiligenceProgressStatus.NotStarted,
  [DueDiligenceProgressStep.CreditApproval]: DueDiligenceProgressStatus.NotStarted,
  [DueDiligenceProgressStep.ContractFinalized]: DueDiligenceProgressStatus.NotStarted,
  [DueDiligenceProgressStep.CollateralDocs]: DueDiligenceProgressStatus.NotStarted,
  [DueDiligenceProgressStep.FirstFunding]: DueDiligenceProgressStatus.NotStarted,
}

export const NON_EDITABLE_DOCUMENTS = [
  'AP Invoice Level Detail Aging Report',
  'AR Invoice Level Detail Aging Report',
  'Bank Statement Reconciliations',
  'Detailed Cap Table',
  'Detailed Inventory Perpetual Report',
  'General Ledger',
  'Historical Income Statement & Balance Sheet',
  'Sales and Gross Margin Monthly by SKU',
  'Forecasted P&L and Balance Sheet',
]

export interface IDueDiligence extends IClientInfo {
  dueDiligenceClientInfo?: IDueDiligenceCompanyInfo
  ddInfo?: IDueDiligenceInfo
  salesforceDDInfo?: ISalesforceDueDiligenceInfo
  managers?: IUser[]
  // BDO owner from the CRM company (salesforce_prospect_info is gone).
  bdo?: IUser | null
  latestBBC?: IBorrowingBase
  primaryContact?: {
    name: string
    salesforceLink: string
  }
}

interface IDueDiligenceDashboradData extends ISalesforceDueDiligenceInfo {
  riskRating: number
  ebitda: number
  netRevenue: number
  progress: number
  maxFinancialsDate: string
  depositStatus: DueDiligenceProgressStatus
}

export interface IDueDiligenceAggregation {
  data: IDueDiligenceDashboradData[]
  totalItems: number
}

export interface IDueDiligenceStats {
  numTermSheets: number
  numDealsInDiligence: number
  dealsClosed: number
  totalCommitments: number
  dealsClosedYtd: number
  avgDaysInDD: number
  avgDaysInDDLastYear: number
  avgDaysInDDGoal: number
  updatedAt: string
}

export interface IDdProspect extends IClientInfo {
  clientName: string
  closeDate?: string
  age?: number
  boxLink?: string
  lastActivityDate?: string
}

export interface IDdProspectsAggregation {
  data: IDdProspect[]
  totalItems: number
  prospects: IDdProspect[]
  numTermSheets: number
  numDealsInDiligence: number
  dealsClosed: number
  dealsClosedYtd: number
  avgDaysinDD: number
  avgDaysinDDLastYear: number
  avgDaysinDDGoal: number
  updatedAt: Date | string
}

export interface IDueDiligenceDataRoomFolder {
  label: string
  value: string
}

export interface ISectionState {
  [key: string]: {
    expanded: boolean
    subSections: {
      [key: string]: boolean
    }
  }
}

export const DUE_DILIGENCE_DOCUMENTS_TYPES = [
  OngoingReportingType.IncomeStatement,

  OngoingReportingType.BalanceSheet,
  OngoingReportingType.ARGeneralLedger,
  OngoingReportingType.SalesBySKU,
  OngoingReportingType.IncomeStatementProjections,
  OngoingReportingType.BalanceSheetProjections,
  OngoingReportingType.AR,
  OngoingReportingType.AP,
  OngoingReportingType.Inventory,
  OngoingReportingType.BankTransactions,
  OngoingReportingType.CapTable,
]

export enum DueDiligenceReportingStatus {
  NotStarted = 'Not started',
  InProgress = 'In progress',
  Completed = 'Completed',
}

export interface IDueDiligenceReportingFlowData {
  recordDate: string | null
  recordDates?: string[]
  startDate?: string
  endDate?: string
  bankAccount?: string
  bankBalance?: number
  error?: string
  arReconcileSummary?: IARAPReconcileSummary
  apReconcileSummary?: IARAPReconcileSummary
}

export interface IDueDiligenceReportingFlow {
  id: string
  clientName: string
  fileId: string
  file: IFile
  isProcessed: boolean
  isCompleted: boolean
  data: IDueDiligenceReportingFlowData
  createdAt: string
  updatedAt: string
  isParent?: boolean
  ddReportingId: string
  dueDiligenceReporting: IDueDiligenceReporting
}

export interface IDueDiligenceReportingFlowsResponse {
  data: IDueDiligenceReportingFlow[]
  dueDiligenceReporting: IDueDiligenceReporting
}

export interface IDueDiligenceReporting {
  id: string
  clientName: string
  type: OngoingReportingType
  status: DueDiligenceReportingStatus
  boxLink: string
  tableauLink: string
  dueDiligenceReportingFlows?: IDueDiligenceReportingFlow[]
  createdAt: string
  updatedAt: string
  submittedDate?: string | null
}

export const DUE_DILIGENCE_DOCUMENTS_TITLE_MAP: Partial<Record<OngoingReportingType, string>> = {
  [OngoingReportingType.IncomeStatement]: 'Historical P&L',
  [OngoingReportingType.BalanceSheet]: 'Historical Balance Sheet',
  [OngoingReportingType.ARGeneralLedger]: 'General Ledger',
  [OngoingReportingType.SalesBySKU]: 'Sales By SKU',
  [OngoingReportingType.IncomeStatementProjections]: 'Projected P&L',
  [OngoingReportingType.BalanceSheetProjections]: 'Projected Balance Sheet',
  [OngoingReportingType.AR]: 'AR Aging Detail Report',
  [OngoingReportingType.AP]: 'AP Aging Details Reports',
  [OngoingReportingType.Inventory]: 'Detailed Inventory Perpetual Report',
  [OngoingReportingType.BankTransactions]: 'Bank Statement',
  [OngoingReportingType.CapTable]: 'Detailed Cap Table',
}

export const DUE_DILIGENCE_DOCUMENTS_FOLDERS_MAP: Partial<Record<OngoingReportingType, string>> = {
  [OngoingReportingType.IncomeStatement]: '04. Financials/a. Historical',
  [OngoingReportingType.BalanceSheet]: '04. Financials/a. Historical',
  [OngoingReportingType.ARGeneralLedger]: '08. Other Reports/General Ledger',
  [OngoingReportingType.SalesBySKU]: '08. Other Reports/Sales by SKU',
  [OngoingReportingType.IncomeStatementProjections]: '04. Financials/b. Forecasted',
  [OngoingReportingType.BalanceSheetProjections]: '04. Financials/b. Forecasted',
  [OngoingReportingType.AR]: '05. AR & AP/a. Receivables',
  [OngoingReportingType.AP]: '05. AR & AP/b. Payables',
  [OngoingReportingType.Inventory]: '06. Inventory/a. Inventory Report',
  [OngoingReportingType.BankTransactions]: '04. Financials/c. Bank Statements',
  [OngoingReportingType.CapTable]: '02. General/b. Cap Table',
}

export const DUE_DILIGENCE_DOCUMENTS_DEFAULT_FOLDER = '08. Other Reports'

export const DUE_DILIGENCE_ROUTES_MAP: Partial<Record<OngoingReportingType, string>> = {
  [OngoingReportingType.AR]: 'receivables',
  [OngoingReportingType.AP]: 'payables',
  [OngoingReportingType.IncomeStatement]: 'income-statement',
  [OngoingReportingType.BalanceSheet]: 'balance-sheet',
  [OngoingReportingType.IncomeStatementProjections]: 'income-statement-projections',
  [OngoingReportingType.BalanceSheetProjections]: 'balance-sheet-projections',
  [OngoingReportingType.BankTransactions]: 'bank-transactions',
  [OngoingReportingType.ARGeneralLedger]: 'ar-general-ledger',
  [OngoingReportingType.SalesBySKU]: 'sales-by-sku',
  [OngoingReportingType.Inventory]: 'inventory',
  [OngoingReportingType.CapTable]: 'cap-table',
}

export const DUE_DILIGENCE_DOCUMENTS_LIST_BREADCRUMBS_MAP: Partial<
  Record<OngoingReportingType, string>
> = {
  [OngoingReportingType.AR]: 'AR Reports',
  [OngoingReportingType.AP]: 'AP Reports',
  [OngoingReportingType.IncomeStatement]: 'Historical P&L',
  [OngoingReportingType.BalanceSheet]: 'Historical Balance Sheet',
  [OngoingReportingType.IncomeStatementProjections]: 'Projected P&L',
  [OngoingReportingType.BalanceSheetProjections]: 'Projected Balance Sheet',
  [OngoingReportingType.BankTransactions]: 'Bank Statement',
  [OngoingReportingType.ARGeneralLedger]: 'General Ledger',
  [OngoingReportingType.SalesBySKU]: 'Sales By SKU',
  [OngoingReportingType.Inventory]: 'Inventory Reports',
  [OngoingReportingType.CapTable]: 'Cap Table',
}

export const DUE_DILIGENCE_DOCUMENTS_LIST_TITLE_MAP: Partial<Record<OngoingReportingType, string>> =
  {
    [OngoingReportingType.AR]: 'AR Aging',
    [OngoingReportingType.AP]: 'AP Aging',
    [OngoingReportingType.IncomeStatement]: 'Historical P&L',
    [OngoingReportingType.BalanceSheet]: 'Historical Balance Sheet',
    [OngoingReportingType.IncomeStatementProjections]: 'Projected P&L',
    [OngoingReportingType.BalanceSheetProjections]: 'Projected Balance Sheet',
    [OngoingReportingType.BankTransactions]: 'Bank Statement(s)',
    [OngoingReportingType.ARGeneralLedger]: 'General Ledger',
    [OngoingReportingType.SalesBySKU]: 'Sales by SKU',
    [OngoingReportingType.Inventory]: 'Inventory',
    [OngoingReportingType.CapTable]: 'Cap Table',
  }

export const DUE_DILIGENCE_TEMPLATES_MAP: Partial<Record<OngoingReportingType, TemplateTypes>> = {
  [OngoingReportingType.IncomeStatement]: TemplateTypes.IncomeStatement,
  [OngoingReportingType.BalanceSheet]: TemplateTypes.BalanceSheet,
  [OngoingReportingType.ARGeneralLedger]: TemplateTypes.ARGeneralLedger,
  [OngoingReportingType.SalesBySKU]: TemplateTypes.SalesBySKU,
  [OngoingReportingType.IncomeStatementProjections]: TemplateTypes.IncomeStatement,
  [OngoingReportingType.BalanceSheetProjections]: TemplateTypes.BalanceSheet,
  [OngoingReportingType.AR]: TemplateTypes.ARDetail,
  [OngoingReportingType.AP]: TemplateTypes.APDetail,
  [OngoingReportingType.Inventory]: TemplateTypes.InventoryDetail,
  [OngoingReportingType.BankTransactions]: TemplateTypes.BankStatement,
  [OngoingReportingType.CapTable]: TemplateTypes.CapTable,
}

export enum DueDiligenceDocumentRequestStatus {
  NotStarted = 'Not started',
  InReview = 'In Review',
  ActionRequired = 'Action Required',
  Completed = 'Completed',
  Waived = 'Waived',
}

/**
 * Statuses in which a client/prospect may still remove a file they uploaded to
 * the diligence data room themselves.
 *
 * Everything else is locked to them: uploading flips the request to In Review
 * (see UploadDocumentRequestsFiles), and from that point the file may already be
 * mapped to financials — deleting it cascades those financials_mappings_raw rows
 * away with the file row. Action Required is the internal team's explicit
 * "replace this" signal (set through UpdateDocumentRequest, which is
 * PORTFOLIO-only), so it re-opens deletion; Completed and Waived stay locked.
 */
export const CLIENT_DELETABLE_DUE_DILIGENCE_STATUSES: readonly DueDiligenceDocumentRequestStatus[] =
  [DueDiligenceDocumentRequestStatus.NotStarted, DueDiligenceDocumentRequestStatus.ActionRequired]

/**
 * Whether a client/prospect may delete their own uploads for a document request.
 *
 * Fails closed: an absent or unrecognised status is treated as locked, because
 * this backs a permission gate (services/files/Delete.ts) where "we could not
 * establish the status" must not read as "go ahead".
 */
export const canClientDeleteDueDiligenceFiles = (
  status: DueDiligenceDocumentRequestStatus | null | undefined,
) => !!status && CLIENT_DELETABLE_DUE_DILIGENCE_STATUSES.includes(status)

export enum DueDiligenceDocumentRequestStep {
  CompanyBackground = 'Company Background',
  Team = 'The Team',
  Financials = 'Financials & Collateral',
}

/** Cast-free narrower for DueDiligenceDocumentRequestStep (see common/helpers/enumGuard). */
export const asDueDiligenceDocumentRequestStep = enumNarrow(DueDiligenceDocumentRequestStep).as

export const COMPANY_BACKGROUND_SUBSTEPS = {
  BusinessInformation: 'Business Information',
  ThirdPartyRelationship: '3rd party relationship',
  ExistingLoans: 'Existing Loan(s)',
  Taxes: 'Taxes',
  Priority: 'Priority',
}

export const TEAM_SUBSTEPS = {
  Ownership: 'Ownership',
  Priority: 'Priority',
}
export const FINANCIALS_SUBSTEPS = {
  Receivables: 'Receivables',
  Payables: 'Payables',
  Inventory: 'Inventory',
  ECommerce: 'eCommerce',
  AccountFinance: 'Account & Finance',
  Priority: 'Priority',
}

export enum DueDiligenceDocumentRequestTypes {
  CapTable = 'CapTable',
  OrgChart = 'OrgChart',
  DriversLicenses = 'DriversLicenses',
  InvestorMarketingMaterials = 'InvestorMarketingMaterials',
  ArticlesOfIncorporation = 'ArticlesOfIncorporation',
  ShareholderAgreement = 'ShareholderAgreement',
  InsuranceSchedules = 'InsuranceSchedules',
  Contracts = 'Contracts',
  LeaseSchedules = 'LeaseSchedules',
  LoanDocumentation = 'LoanDocumentation',
  LicensingAgreements = 'LicensingAgreements',
  BalanceSheet = 'BalanceSheet',
  BalanceSheetProjections = 'BalanceSheetProjections',
  BankTransactions = 'BankTransactions',
  TrialBalance = 'TrialBalance',
  KeyWholesale = 'KeyWholesale',
  SalesByCustomer = 'SalesByCustomer',
  AR = 'ARAging',
  AP = 'APAging',
  Inventory = 'InventoryDetail',
  SalesBySKU = 'SalesBySKU',
  CorporateTaxReturns = 'CorporateTaxReturns',
  PayrollTaxReturns = 'PayrollTaxReturns',
  TaxGuardForm = 'TaxGuardForm',
  TaxPaymentsSchedule = 'TaxPaymentsSchedule',
  CurrentOpenOrders = 'CurrentOpenOrders',
  ARGeneralLedger = 'ARGeneralLedger',
  BankTransactionsReconciliations = 'BankTransactionsReconciliations',
  CancelledChecks = 'CancelledChecks',
  CashReceiptsJournal = 'CashReceiptsJournal',
  CashDisbursementsJournal = 'CashDisbursementsJournal',
  SalesJournal = 'SalesJournal',
  CreditMemoJournal = 'CreditMemoJournal',
  DebitMemoJournal = 'DebitMemoJournal',
  PurchasesJournal = 'PurchasesJournal',
  InventoryRollforwardAnalysis = 'InventoryRollforwardAnalysis',
  PurchasesByVendor = 'PurchasesByVendor',
  LatestBorrowingBaseCertificate = 'LatestBorrowingBaseCertificate',
  HeldChecks = 'HeldChecks',
  CashApplicationSample = 'CashApplicationSample',
  CashDisbursementSample = 'CashDisbursementSample',
  BillingShippingSample = 'BillingShippingSample',
  CreditMemoSample = 'CreditMemoSample',
  ARVerificationSample = 'ARVerificationSample',
  InventoryCountSample = 'InventoryCountSample',
  VendorCostSample = 'VendorCostSample',
  GrossProfitSample = 'GrossProfitSample',
  SalesTaxList = 'SalesTaxList',
  StateSalesTaxSample = 'StateSalesTaxSample',
}

export interface IDueDiligenceDocumentRequestType {
  id: string
  type: DueDiligenceDocumentRequestTypes
  name: string
  step: DueDiligenceDocumentRequestStep
  subStep: string
  helperText: string | null
  fileType: string | null
  isSample: boolean
  folderPath?: string
}

export interface IDueDiligenceDocumentRequestComment {
  id: string
  comment: string
  userId: string
  user?: IUser
  createdAt: string
  updatedAt: string
}

export interface IDueDiligenceDocumentRequest {
  id: string
  clientName: string
  type: IDueDiligenceDocumentRequestType
  status: DueDiligenceDocumentRequestStatus
  previousStatus: DueDiligenceDocumentRequestStatus | null
  boxLink: string
  files: IFile[]
  comments: IDueDiligenceDocumentRequestComment[]
  createdAt: string
  updatedAt: string
  submittedDate?: string | null
}

export interface IDueDiligenceCompanyInfo {
  id: string
  clientName: string
  companyBackgroundProgress: number
  teamProgress: number
  financialCollateralProgress: number
  companyInfoProgressRedesign?: number
  teamProgressRedesign?: number
  referencesProgressRedesign?: number
  inventoryProgressRedesign?: number
  bankingProgressRedesign?: number
  partnersProgressRedesign?: number
  applicationStepsProgress?: number
  priorityDocumentsProgress?: number
  fullLegalName: string | null
  employeeCount: number | null
  /** Archived V1 form answers. Nothing writes these — prefer the derived counts below. */
  legacyExecutivesCount: number | null
  legacyBoardMembersCount: number | null
  /**
   * Computed per-request by the Show service, never stored. Null for executives means no
   * executive has been asked the ownership question yet, which is why the display falls
   * back to the legacy value rather than showing 0.
   */
  derivedExecutivesCount?: number | null
  derivedBoardMembersCount?: number
  billingPostalCode: string | null
  billingState: string | null
  billingCity: string | null
  billingCountry: string | null
  billingStreet: string | null
  establishedDate: string | null
  phone: string | null
  stateOfFormation: string | null
  federalTaxId: string | null
  isWithAllTaxes: boolean
  isWithAllTaxesComment: string | null
  isPendingJudgement: boolean
  isPendingJudgementComment: string | null
  isAssetsPledged: boolean
  isAssetsPledgedComment: string | null
  erpSystem: ClientERPSystem[]
  erpSystemOther: string | null
  hasContacts: boolean
  isAllContactsValid: boolean
  hasExecutives: boolean
  isAllExecutivesValid: boolean
  hasBoardMembers: boolean
  isAllBoardMembersValid: boolean
  hasReferences: boolean
  isAllReferencesValid: boolean
  hasInventoryLocations: boolean
  isAllInventoryLocationsValid: boolean
  hasBankAccounts: boolean
  clientEntityRelationship?: IClientEntityRelationship[]
  isOutsourcedCFO: boolean
}

export enum DueDiligenceTableItemType {
  Contact = 'Contact',
  Executive = 'Executive',
  BoardMember = 'Board member',
  Reference = 'Reference',
  InventoryLocation = 'Inventory location',
  BankAccount = 'Bank Account',
  Advisor = 'Advisor',
}

export interface IDueDiligenceContact {
  id: string
  clientName: string
  firstName: string
  lastName: string
  email: string
  phone: string
  title: string
  isResponsibleRequestingFunding: boolean
  isAuthorizedSignature: boolean
  inviteAsUser?: boolean
  inviteStatus?: UserStatus | 'Invitation sent' | null
  user?: {
    id: string
  }
}

export interface IDueDiligenceExecutive {
  id: string
  clientName: string
  firstName: string
  lastName: string
  title: string
  email: string
  phone: string
  socialSecurityNumber: string
  birthday: string
  postalCode: string | null
  state: string | null
  city: string | null
  country: string | null
  street: string | null
  isConvictedFelony: boolean
  isConvictedFelonyComment: string | null
  isDefaultedFinancialObligation: boolean
  isDefaultedFinancialObligationComment: string | null
  isPersonalBankruptcy: boolean
  isPersonalBankruptcyComment: string | null
  homeAddress?: {
    country?: string
    state?: string
    city?: string
    postalCode?: string
    street?: string
  }
  linkedBoardMemberId?: string
  isBoardMember?: boolean
  isFivePercentOwner?: boolean | null
}

export interface IDueDiligenceBoardMember {
  id: string
  email: string
  clientName: string
  firstName: string
  lastName: string
  company: string
  relationshipToCompany: string
}

export interface IDueDiligenceReference {
  id: string
  clientName: string
  firstName: string
  lastName: string
  email: string
  phone: string
  businessName: string
  relationshipToCompany: string
}

export interface IDueDiligenceInventoryLocation {
  id: string
  clientName: string
  name: string
  type: string
  landlord: string
  postalCode: string | null
  state: string | null
  city: string | null
  country: string | null
  street: string | null
  finishedGoodsAmount: number
  totalInventoryAmount: number
  businessAddress?: {
    country?: string
    state?: string
    city?: string
    postalCode?: string
    street?: string
  }
}

export interface IDueDiligenceBankAccount {
  id: string
  clientName: string
  bankName: string
  accountHolderName: string
  bankAccountNumber: string
  abaRoutingNumber: string
  purpose: string
  // Populated from a linked Plaid account when the prospect connects via Plaid.
  isPlaidLinked?: boolean
  currentBalance?: string | null
  availableBalance?: string | null
  isoCurrencyCode?: string | null
  plaidUpdatedAt?: string | Date | null
  plaidItemId?: string | null
  plaidLastSyncedAt?: string | Date | null
}

export type IDueDiligenceTableItem =
  | IDueDiligenceContact
  | IDueDiligenceExecutive
  | IDueDiligenceBoardMember
  | IDueDiligenceReference
  | IDueDiligenceInventoryLocation
  | IDueDiligenceBankAccount

export const DUE_DILIGENCE_DOCUMENT_REQUEST_STATUS_OPTIONS = [
  {
    value: DueDiligenceDocumentRequestStatus.NotStarted,
    label: 'Not Started',
  },
  {
    value: DueDiligenceDocumentRequestStatus.InReview,
    label: 'In Review',
  },
  {
    value: DueDiligenceDocumentRequestStatus.ActionRequired,
    label: 'Action Required',
  },
  {
    value: DueDiligenceDocumentRequestStatus.Completed,
    label: 'Completed',
  },
  {
    value: DueDiligenceDocumentRequestStatus.Waived,
    label: 'Waived',
  },
]

export const DUE_DILIGENCE_STEP_STATUS_OPTIONS = [
  {
    value: DueDiligenceProgressStatus.NotStarted,
    label: 'Not Started',
  },
  {
    value: DueDiligenceProgressStatus.InProgress,
    label: 'In Progress',
  },
  {
    value: DueDiligenceProgressStatus.Completed,
    label: 'Completed',
  },
  {
    value: DueDiligenceProgressStatus.NotApplicable,
    label: 'Not Applicable',
  },
]

/** The deposit step's own vocabulary; stored as the matching step statuses. */
export const DILIGENCE_DEPOSIT_STATUS_OPTIONS = [
  { value: DueDiligenceProgressStatus.NotStarted, label: 'Not received' },
  { value: DueDiligenceProgressStatus.InProgress, label: 'Pending' },
  { value: DueDiligenceProgressStatus.Completed, label: 'Received' },
]

export const DUE_DILIGENCE_DOCUMENTS_TYPES_WITHOUT_UPLOAD = [
  DueDiligenceDocumentRequestTypes.BankTransactions,
  DueDiligenceDocumentRequestTypes.KeyWholesale,
]

export const DUE_DILIGENCE_DOCUMENTS_TYPES_WITH_TEMPLATES = [
  DueDiligenceDocumentRequestTypes.AR,
  DueDiligenceDocumentRequestTypes.AP,
  DueDiligenceDocumentRequestTypes.Inventory,
  DueDiligenceDocumentRequestTypes.SalesBySKU,
]

export const AUTO_REPORTING_DOCUMENTS = [
  DueDiligenceDocumentRequestTypes.AR,
  DueDiligenceDocumentRequestTypes.AP,
  DueDiligenceDocumentRequestTypes.BalanceSheet,
]

export const DUE_DILIGENCE_DOCUMENTS_TYPES_SAMPLES = [
  DueDiligenceDocumentRequestTypes.CashApplicationSample,
  DueDiligenceDocumentRequestTypes.CashDisbursementSample,
  DueDiligenceDocumentRequestTypes.BillingShippingSample,
  DueDiligenceDocumentRequestTypes.CreditMemoSample,
  DueDiligenceDocumentRequestTypes.ARVerificationSample,
  DueDiligenceDocumentRequestTypes.InventoryCountSample,
  DueDiligenceDocumentRequestTypes.VendorCostSample,
  DueDiligenceDocumentRequestTypes.GrossProfitSample,
  DueDiligenceDocumentRequestTypes.StateSalesTaxSample,
]

export const DUE_DILIGENCE_TABLE_ITEM_REQUIRED_FIELDS: Partial<
  Record<DueDiligenceTableItemType, string[]>
> = {
  [DueDiligenceTableItemType.Contact]: ['firstName', 'lastName', 'title', 'phone'],
  [DueDiligenceTableItemType.Executive]: [
    'firstName',
    'lastName',
    'phone',
    'email',
    'title',
    'socialSecurityNumber',
    'birthday',
    'street',
    'city',
    'state',
    'postalCode',
    'country',
  ],
  [DueDiligenceTableItemType.BoardMember]: [
    'firstName',
    'lastName',
    'email',
    'company',
    'relationshipToCompany',
  ],
  [DueDiligenceTableItemType.Reference]: [
    'firstName',
    'lastName',
    'phone',
    'email',
    'businessName',
    'relationshipToCompany',
  ],
  [DueDiligenceTableItemType.InventoryLocation]: [
    'name',
    'type',
    'landlord',
    'finishedGoodsAmount',
    'totalInventoryAmount',
    'street',
    'city',
    'state',
    'postalCode',
    'country',
  ],
  [DueDiligenceTableItemType.BankAccount]: [
    'bankName',
    'accountHolderName',
    'bankAccountNumber',
    'abaRoutingNumber',
    'purpose',
  ],
  [DueDiligenceTableItemType.Advisor]: ['firm', 'firstName', 'lastName', 'email'],
}

export const DUE_DILIGENCE_TABLE_ITEM_FIELD_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  title: 'Title',
  email: 'Email',
  phone: 'Phone number',
  socialSecurityNumber: 'Social security number',
  birthday: 'Date of birth',
  street: 'Street',
  city: 'City',
  state: 'State',
  postalCode: 'Zip',
  company: 'Company',
  businessName: 'Business name',
  relationshipToCompany: 'Relationship to company',
  name: 'Name',
  type: 'Location type',
  landlord: 'Landlord',
  finishedGoodsAmount: 'Finished goods value',
  totalInventoryAmount: 'Total inventory value',
  bankName: 'Bank name',
  accountHolderName: 'Account holder',
  bankAccountNumber: 'Bank account #',
  abaRoutingNumber: 'Wire routing #',
  purpose: 'Purpose',
}

export const DUE_DILIGENCE_STATUS_SORT_ORDER = {
  [DueDiligenceDocumentRequestStatus.ActionRequired]: 1,
  [DueDiligenceDocumentRequestStatus.InReview]: 2,
  [DueDiligenceDocumentRequestStatus.NotStarted]: 3,
  [DueDiligenceDocumentRequestStatus.Completed]: 4,
  [DueDiligenceDocumentRequestStatus.Waived]: 5,
}

export const DUE_DILIGENCE_DOCUMENTS_WITH_AUTO_REFRESH = [
  OngoingReportingType.AR,
  OngoingReportingType.AP,
  OngoingReportingType.IncomeStatement,
  OngoingReportingType.BalanceSheet,
  OngoingReportingType.ARGeneralLedger,
]

export enum DueDiligenceHeaderMenuPages {
  LCR = 'LCR',
  DiligenceProfile = 'DiligenceProfile',
  ProcessDocuments = 'ProcessDocuments',
}

export interface IHistoricalLineTurn {
  recordDate: string
  collections: number
  /** Reported balances the availability was estimated from — no eligibility,
   *  cross-age, concentration or sublimit adjustments applied. */
  accountsReceivable: number | null
  inventory: number | null
  /** AR x arAdvanceRate + Inventory x inventoryAdvanceRate. */
  estimatedAvailability: number | null
  /** collections / estimatedAvailability. `null` where the month has no
   *  reported balance, so the chart draws a gap instead of a zero. */
  lineTurn: number | null
}

export interface IHistoricalLineTurnTotals {
  minLineTurn: number
  maxLineTurn: number
  avgLineTurn: number
}

/** The advance rates a line-turn series was computed with. */
export interface IHistoricalLineTurnAssumptions {
  arAdvanceRate: number
  inventoryAdvanceRate: number
  /** Rates recorded on the deal, when it has them — what "Reset to deal
   *  terms" restores, and `null` when the deal is silent and the chart is
   *  running on the underwriting defaults. */
  dealArAdvanceRate: number | null
  dealInventoryAdvanceRate: number | null
}

export interface IHistoricalLineTurnData {
  data: IHistoricalLineTurn[]
  totals: IHistoricalLineTurnTotals
  assumptions: IHistoricalLineTurnAssumptions
}

export interface IHistoricalLineTurnOptionsData {
  /** Span the data actually covers — advisory, not a hard picker bound. */
  minDate: string
  maxDate: string
  /** Client's contract date, the hard lower bound for the date picker. */
  contractDate: string | null
}

export interface IDefaultNoteOptionsData {
  tags: string[]
}

export interface IDefaultNote {
  id: string
  note: RawDraftContentState
  noteText: string
  order: number
  tags: ITag[]
  createdAt: Date
  noteTextUpdatedAt: string
}

export const UW_REPORT_ORDER = [
  OngoingReportingType.AR,
  OngoingReportingType.AP,
  OngoingReportingType.Inventory,
  OngoingReportingType.SalesBySKU,
  OngoingReportingType.IncomeStatement,
  OngoingReportingType.BalanceSheet,
  OngoingReportingType.IncomeStatementProjections,
  OngoingReportingType.BalanceSheetProjections,
  OngoingReportingType.ARGeneralLedger,
  OngoingReportingType.BankTransactions,
  OngoingReportingType.CapTable,
]
